import { OrganizationStatus } from '@repo/shared'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  useIsMobile,
} from '@repo/ui'
import { useDebouncedValue } from '@tanstack/react-pacer'
import { Link, createFileRoute } from '@tanstack/react-router'
import { Plus, Search } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { AdminOrganizationsListParams } from '@repo/api'
import type { SortState } from '@/components/shared/sortable-table-head'

import { ListPagination } from '@/components/shared/list-pagination'
import { toggleSort } from '@/components/shared/sortable-table-head'
import { OrganizationCard } from '@/features/admin/organizations/components/organization-card'
import { OrganizationsTable } from '@/features/admin/organizations/components/organizations-table'
import { useAdminOrganizationsPaginated } from '@/features/admin/organizations/hooks/use-admin-organizations'

export const Route = createFileRoute('/admin/organizations/')({
  component: AdminOrganizationsListPage,
})

const ALL = 'all'

type SortKey = NonNullable<AdminOrganizationsListParams['sort']>

const isSortKey = (key: string): key is SortKey =>
  ['name', 'timezone', 'status', 'memberCount', 'createdAt'].includes(key)

function AdminOrganizationsListPage() {
  const { t } = useTranslation(['admin', 'common'])
  const isMobile = useIsMobile()

  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebouncedValue(search, { wait: 300 })
  const [statusFilter, setStatusFilter] = useState<string>(ALL)
  const [sort, setSort] = useState<SortState>({
    key: 'name',
    direction: 'asc',
  })
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const sortKey: SortKey =
    sort && isSortKey(sort.key) ? sort.key : 'name'

  const { data, isPending, isFetching } = useAdminOrganizationsPaginated({
    page,
    pageSize,
    search: debouncedSearch.trim() || undefined,
    status:
      statusFilter !== ALL ? (statusFilter as OrganizationStatus) : undefined,
    sort: sortKey,
    sortDir: sort?.direction ?? 'asc',
  })

  const handleSort = (key: string) => {
    setSort((prev) => toggleSort(prev, key))
    setPage(1)
  }

  const items = data?.items ?? []
  const totalCount = data?.totalCount ?? 0
  const hasFilters =
    debouncedSearch.trim().length > 0 || statusFilter !== ALL

  return (
    <div className="space-y-4">
      <Card className="bg-background">
        <CardHeader>
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle>{t('organizations.list.title')}</CardTitle>
              <CardDescription>
                {totalCount} {t('organizations.list.totalCount')}
              </CardDescription>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="relative w-full md:w-64">
                <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
                <Input
                  placeholder={t('organizations.list.searchPlaceholder')}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value)
                    setPage(1)
                  }}
                  className="h-11 pl-9"
                />
              </div>

              <Select
                value={statusFilter}
                onValueChange={(v) => {
                  setStatusFilter(v)
                  setPage(1)
                }}
              >
                <SelectTrigger className="h-11 w-full sm:w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>
                    {t('organizations.list.allStatuses')}
                  </SelectItem>
                  {Object.values(OrganizationStatus).map((s) => (
                    <SelectItem key={s} value={s}>
                      {t(`organizations.statuses.${s}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button asChild size="lg" className="primary-bg-gradient">
                <Link to="/admin/organizations/create">
                  <Plus className="size-4" />
                  {t('organizations.list.create')}
                </Link>
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {isPending ? (
            isMobile ? (
              <div className="space-y-2">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-24 w-full rounded-md" />
                ))}
              </div>
            ) : (
              <div className="overflow-hidden rounded-lg border">
                <Skeleton className="h-10 w-full rounded-none" />
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton
                    key={i}
                    className="h-14 w-full rounded-none border-t"
                  />
                ))}
              </div>
            )
          ) : items.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-muted-foreground text-sm">
                {hasFilters
                  ? t('organizations.list.noMatches')
                  : t('organizations.list.empty')}
              </p>
            </div>
          ) : (
            <div
              className={isFetching ? 'opacity-60 transition-opacity' : undefined}
            >
              {isMobile ? (
                <div className="space-y-2">
                  {items.map((org) => (
                    <OrganizationCard key={org.id} organization={org} />
                  ))}
                </div>
              ) : (
                <OrganizationsTable
                  organizations={items}
                  sort={sort}
                  onSort={handleSort}
                />
              )}

              <ListPagination
                page={data!.page}
                totalPages={data!.totalPages}
                pageSize={data!.pageSize}
                totalCount={data!.totalCount}
                hasPrevious={data!.hasPrevious}
                hasNext={data!.hasNext}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                  setPageSize(size)
                  setPage(1)
                }}
                className="mt-4"
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
